import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { CostCentre } from "../../api/types";

type CostCentreRow = CostCentre & { active: boolean };

const TYPE_LABEL: Record<string, string> = {
  home: "Home",
  support_office: "Support Office",
  region: "Region",
};

const TYPE_ORDER = ["support_office", "region", "home"];

const SUB_BRAND_LABEL: Record<string, string> = {
  residential: "Residential",
  supported_living: "Supported Living",
  liaise_life: "Liaise Life",
};

const SUB_BRAND_PILL: Record<string, string> = {
  residential: "bg-liaise-green/10 text-liaise-green",
  supported_living: "bg-liaise-orange/10 text-liaise-orange",
  liaise_life: "bg-liaise-purple/10 text-liaise-purple",
};

const blank: Partial<CostCentreRow> = {
  code: "",
  name: "",
  type: "home",
  parent_id: null,
  sub_brand: null,
  active: true,
};

export default function CostCentresAdmin() {
  const qc = useQueryClient();
  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ["admin-cost-centres"],
    queryFn: () => api.admin.listCostCentres() as unknown as Promise<CostCentreRow[]>,
  });
  const [editing, setEditing] = useState<Partial<CostCentreRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const save = useMutation({
    mutationFn: (cc: Partial<CostCentreRow>) =>
      cc.id ? api.admin.updateCostCentre(cc.id, cc) : api.admin.createCostCentre(cc),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-cost-centres"] });
      qc.invalidateQueries({ queryKey: ["cost-centres"] });
      setEditing(null);
      setError(null);
    },
    onError: (e) => setError((e as Error).message),
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => api.admin.deactivateCostCentre(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-cost-centres"] }),
  });

  const grouped: Record<string, CostCentreRow[]> = {};
  if (data) {
    const filtered = (data as CostCentreRow[]).filter((cc) =>
      filter ? `${cc.code} ${cc.name}`.toLowerCase().includes(filter.toLowerCase()) : true,
    );
    for (const cc of filtered) {
      if (!grouped[cc.type]) grouped[cc.type] = [];
      grouped[cc.type].push(cc);
    }
  }

  const regions = (data as CostCentreRow[] | undefined)?.filter((cc) => cc.type === "region" && cc.active) ?? [];

  return (
    <div className="space-y-6">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Back to admin</Link>

      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-liaise-navy">Cost centres</h1>
          <p className="text-slate-600 mt-1">Homes, Support Office sub-units, and regional groupings used across the app.</p>
        </div>
        <button
          onClick={() => { setEditing({ ...blank }); setError(null); }}
          className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700"
        >
          + New cost centre
        </button>
      </div>

      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter by code or name…"
        className="w-full max-w-md border border-slate-300 rounded-lg px-3 py-2 text-sm"
      />

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {isLoading && <div className="px-6 py-12 text-center text-slate-500">Loading…</div>}
        {loadError && (
          <div className="px-6 py-8 text-red-700 bg-red-50 border-b border-red-200 text-sm">
            <div className="font-medium">Failed to load cost centres</div>
            <div className="font-mono text-xs mt-1">{(loadError as Error).message}</div>
          </div>
        )}
        {data && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-6 py-3 font-medium">Code</th>
                <th className="px-6 py-3 font-medium">Name</th>
                <th className="px-6 py-3 font-medium">Sub-brand</th>
                <th className="px-6 py-3 font-medium">Active</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {TYPE_ORDER.filter((t) => grouped[t]?.length).map((type) => (
                <Fragment key={type}>
                  <tr className="bg-slate-100">
                    <td colSpan={5} className="px-6 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                      {TYPE_LABEL[type]} ({grouped[type].length})
                    </td>
                  </tr>
                  {grouped[type].map((cc) => (
                    <tr key={cc.id} className={!cc.active ? "bg-slate-50/50 text-slate-400" : ""}>
                      <td className="px-6 py-2.5 font-mono text-xs">{cc.code}</td>
                      <td className="px-6 py-2.5">{cc.name}</td>
                      <td className="px-6 py-2.5">
                        {cc.sub_brand ? (
                          <span className={`text-xs px-2 py-0.5 rounded ${SUB_BRAND_PILL[cc.sub_brand] ?? ""}`}>
                            {SUB_BRAND_LABEL[cc.sub_brand]}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-6 py-2.5">{cc.active ? "✓" : "—"}</td>
                      <td className="px-6 py-2.5 text-right space-x-3 whitespace-nowrap">
                        <button onClick={() => { setEditing({ ...cc }); setError(null); }} className="text-liaise-navy text-xs hover:underline">Edit</button>
                        {cc.active && (
                          <button onClick={() => deactivate.mutate(cc.id)} className="text-red-600 text-xs hover:underline">Deactivate</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold text-liaise-navy">
              {editing.id ? "Edit cost centre" : "New cost centre"}
            </h2>

            <div className="grid grid-cols-3 gap-3">
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Code</span>
                <input
                  value={editing.code ?? ""}
                  onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 font-mono text-sm"
                />
              </label>
              <label className="text-sm col-span-2">
                <span className="block font-medium text-slate-700 mb-1">Name</span>
                <input
                  value={editing.name ?? ""}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Type</span>
                <select
                  value={editing.type ?? "home"}
                  onChange={(e) => setEditing({ ...editing, type: e.target.value as CostCentre["type"] })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                >
                  <option value="home">Home</option>
                  <option value="support_office">Support Office</option>
                  <option value="region">Region</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block font-medium text-slate-700 mb-1">Sub-brand (homes only)</span>
                <select
                  value={editing.sub_brand ?? ""}
                  onChange={(e) => setEditing({ ...editing, sub_brand: e.target.value === "" ? null : e.target.value as CostCentre["sub_brand"] })}
                  disabled={editing.type !== "home"}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 disabled:bg-slate-50"
                >
                  <option value="">—</option>
                  <option value="residential">Residential</option>
                  <option value="supported_living">Supported Living</option>
                  <option value="liaise_life">Liaise Life</option>
                </select>
              </label>
            </div>

            <label className="text-sm block">
              <span className="block font-medium text-slate-700 mb-1">Parent region (optional)</span>
              <select
                value={editing.parent_id ?? ""}
                onChange={(e) => setEditing({ ...editing, parent_id: e.target.value === "" ? null : Number(e.target.value) })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
              >
                <option value="">—</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </label>

            <label className="text-sm flex items-center gap-2">
              <input
                type="checkbox"
                checked={editing.active ?? true}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
              />
              <span>Active</span>
            </label>

            {error && <div className="text-red-600 text-sm">{error}</div>}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => save.mutate(editing)}
                disabled={!editing.code || !editing.name || !editing.type || save.isPending}
                className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50"
              >
                {save.isPending ? "Saving…" : "Save"}
              </button>
              <button onClick={() => setEditing(null)} className="text-slate-600 px-4 py-2 hover:underline">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
